import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useContent } from "@/content/ContentContext";
import { useLang } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

const TAGLINES = [
  "Har Boond Mein Vikas.",
  "More Crop Per Drop.",
  "Better Yields. Greater Profits.",
  "Solution for Generations.",
  "One Stop Irrigation Solutions.",
];

export default function Hero() {
  const content = useContent();
  const { t } = useLang();
  const h = content.hero;
  const ref = useRef(null);
  const [tagIdx, setTagIdx] = useState(0);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    const id = setInterval(() => setTagIdx((i) => (i + 1) % TAGLINES.length), 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen items-end overflow-hidden bg-[#0B1A33]"
    >
      <motion.div style={{ y }} className="absolute inset-0" aria-hidden="true">
        <motion.img
          src={h.image}
          alt="Jamna Industries irrigation systems in the field"
          className="h-full w-full object-cover"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-[#0B1A33]/55" aria-hidden="true" />
      <div
        className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-forest to-transparent"
        aria-hidden="true"
      />

      <motion.div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 z-20 origin-top bg-black"
        initial={{ height: "14vh" }}
        animate={{ height: 0 }}
        transition={{ duration: 1.4, delay: 0.15, ease }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 z-20 origin-bottom bg-black"
        initial={{ height: "14vh" }}
        animate={{ height: 0 }}
        transition={{ duration: 1.4, delay: 0.15, ease }}
      />

      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-28 pt-44 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.7, ease }}
          data-testid="hero-overline"
          className="mb-8 text-xs uppercase tracking-[0.3em] text-harvestHover"
        >
          {h.overline}
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display text-5xl font-medium leading-[0.98] tracking-tighter text-white sm:text-7xl lg:text-8xl"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ delay: 0.75, duration: 1, ease }}
            >
              {h.line1}
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ delay: 0.9, duration: 1, ease }}
            >
              {h.line2a} <span className="italic text-harvestHover">{h.line2b}</span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.8, ease }}
          data-testid="hero-subheadline"
          className="mt-8 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg"
        >
          {h.sub}
          <span className="mt-4 block min-h-[2.25rem]">
            <AnimatePresence mode="wait">
              <motion.span
                key={tagIdx}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.5, ease }}
                data-testid="hero-tagline"
                className="font-display text-xl font-semibold italic text-harvestHover sm:text-2xl"
              >
                {TAGLINES[tagIdx]}
              </motion.span>
            </AnimatePresence>
          </span>
          <span className="mt-3 flex gap-2" aria-hidden="true">
            {TAGLINES.map((_, i) => (
              <span
                key={i}
                className={`h-1 w-6 transition-colors duration-500 ${i === tagIdx ? "bg-harvestHover" : "bg-white/25"}`}
              />
            ))}
          </span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.8, ease }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/products"
            data-testid="hero-explore-button"
            className="group inline-flex items-center gap-3 bg-harvest px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-harvestHover"
          >
            {t("heroExplore")}
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-enquire-button"
            className="group inline-flex items-center gap-3 border border-white/40 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-harvestHover hover:text-harvestHover"
          >
            {t("heroEnquire")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        data-testid="hero-scroll-cue"
        className="absolute bottom-10 right-6 z-10 hidden flex-col items-center gap-3 sm:flex lg:right-12"
        aria-hidden="true"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/60 [writing-mode:vertical-rl]">
          {t("scroll")}
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="block h-10 w-px bg-white/40"
        />
      </motion.div>
    </section>
  );
}
